import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, formatDT, STATUS_STYLES } from '../lib/supabase';

function StatusPill({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES['Open'];
  return (
    <span style={{
      display:'inline-block', padding:'3px 10px', borderRadius:20,
      fontSize:11, fontWeight:600, background:style.bg, color:style.color
    }}>
      {status}
    </span>
  );
}

function POField({ load, onPOAdded }) {
  const [poNums, setPoNums]   = useState(
    load.po_num ? load.po_num.split(',').map(p => p.trim()).filter(Boolean) : []
  );
  const [pending, setPending] = useState([]);
  const [newPO, setNewPO]     = useState('');
  const [adding, setAdding]   = useState(false);

  async function handleAdd() {
    const trimmed = newPO.trim();
    if (!trimmed) return;

    const updated = [...poNums, trimmed];
    const { error } = await supabase
    .from('loads')
    .update({ po_num: updated.join(',') })
    .eq('id', load.id);

    console.log('Save error:', error); // ADD THIS
    console.log('Updated POs:', updated); // AND THIS

    if (!error) {
      setPoNums(updated);
      setPending(p => [...p, trimmed]);
      setNewPO('');
      onPOAdded && onPOAdded(load, updated);
    }
  }

  async function handleDelete(po) {
    const updated = poNums.filter(p => p !== po);
    const { error } = await supabase
      .from('loads')
      .update({ po_num: updated.length ? updated.join(',') : null })
      .eq('id', load.id);

    if (!error) setPoNums(updated);
  }

  return (
    <div style={{fontSize:12}}>
      {/* EXISTING PO#S */}
      <div style={{display:'flex', flexWrap:'wrap', gap:4, marginBottom:6}}>
        {poNums.map(po => (
          <span key={po} style={{
            display:'inline-flex', alignItems:'center', gap:4,
            background: pending.includes(po) ? '#fff3cd' : '#e8ecf5',
            borderRadius:12, padding:'2px 8px', fontSize:11, fontWeight:500,
            color: pending.includes(po) ? '#7a5500' : 'inherit'
          }}>
            {po}
            {pending.includes(po) && <span style={{fontSize:10}}>(pending)</span>}
            <button
              onClick={() => handleDelete(po)}
              style={{background:'none', border:'none', cursor:'pointer',
                      color:'#999', fontSize:12, padding:0, lineHeight:1}}
            >✕</button>
          </span>
        ))}
      </div>

      {/* ADD INPUT */}
      {adding ? (
        <div>
          <p style={{fontSize:11, color:'#555', marginBottom:4}}>
            Add all PO#s for this load, then use <strong>Submit All</strong> below
          </p>
          <div style={{display:'flex', gap:6, alignItems:'center'}}>
            <input
              value={newPO}
              onChange={e => setNewPO(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="Enter PO#"
              style={{fontSize:12, padding:'4px 8px', border:'1px solid #d0d5e8',
                      borderRadius:4, width:120}}
              autoFocus
            />
            <button
              onClick={handleAdd}
              disabled={!newPO.trim()}
              style={{background:'#e8ecf5', color:'#000F9F', border:'none',
                      borderRadius:4, padding:'4px 10px', fontSize:11,
                      cursor: !newPO.trim() ? 'not-allowed' : 'pointer',
                      opacity: !newPO.trim() ? 0.6 : 1}}
            >+ Add</button>
            <button
              onClick={() => { setAdding(false); setNewPO(''); }}
              style={{background:'none', border:'none', cursor:'pointer',
                      color:'#999', fontSize:11}}
            >Done</button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          style={{background:'none', border:'none', cursor:'pointer',
                  color:'#000F9F', fontSize:11, padding:'2px 4px'}}
        >+ Add PO#</button>
      )}
    </div>
  );
}

export default function CustomerView() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | valid | expired | error
  const [entity, setEntity] = useState(null);
  const [loads, setLoads] = useState([]);

  // SET UP GROWL TOAST
  const [dirtyLoads, setDirtyLoads] = useState({});
  const [toast, setToast]           = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);

  useEffect(() => {
    async function validate() {
      // Find magic link
      const { data: link, error } = await supabase
        .from('magic_links')
        .select('*, entities(*)')
        .eq('token', token)
        .single();

      if (error || !link) { setState('error'); return; }

      if (new Date(link.expires_at) < new Date()) { setState('expired'); return; }

      setEntity(link.entities);

      // Fetch 30 days of loads for this entity
      const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data: loadData } = await supabase
        .from('loads')
        .select('*')
        .eq('entity_id', link.entities.id)
        .eq('excluded', false)
        .or(`status.in.(Open,Booked,Dispatched,In Transit,Opportunity),and(status.eq.Delivered,delivered_at.gte.${cutoff})`)
        .order('delivered_at', { ascending: false });

      setLoads(loadData || []);
      setState('valid');
    }
    validate();
  }, [token]);

  function handlePOAdded(load, updatedPoNums) {
    setDirtyLoads(prev => ({
      ...prev,
      [load.id]: { load, poNums: updatedPoNums }
    }));
  }

  async function handleSubmitAll() {
    setSubmitting(true);
    const changes = Object.values(dirtyLoads);

    await fetch('https://arpjtibkfckuzkxwguaj.supabase.co/functions/v1/notify-ops', {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({ changes })
    });

    setDirtyLoads({});

    // RELOAD LOADS TO REFLECT SAVED PO#S
    const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const { data: loadData } = await supabase
      .from('loads')
      .select('*')
      .eq('entity_id', entity.id)
      .eq('excluded', false)
      .or(`status.in.(Open,Booked,Dispatched,In Transit,Opportunity),and(status.eq.Delivered,delivered_at.gte.${cutoff})`)
      .order('load_num', { ascending: true });

    setLoads(loadData || []);

    setSessionKey(k => k + 1);
    setSubmitting(false);
    setToast(true);
    setTimeout(() => setToast(false), 3000);
  }

  if (state === 'loading') return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#f0f2f8'}}>
      <div style={{textAlign:'center'}}>
        <div style={{width:32,height:32,border:'3px solid #e2e5f0',borderTopColor:'#000F9F',borderRadius:'50%',animation:'spin 0.6s linear infinite',margin:'0 auto 12px'}}></div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <p style={{color:'#6b7280',fontSize:13}}>Loading your shipments...</p>
      </div>
    </div>
  );

  if (state === 'expired') return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#f0f2f8'}}>
      <div style={{textAlign:'center',background:'#fff',padding:40,borderRadius:10,maxWidth:400}}>
        <div style={{fontSize:40,marginBottom:12}}>⏱</div>
        <h2 style={{fontFamily:'Syne,sans-serif',color:'#000F9F',marginBottom:8}}>Link Expired</h2>
        <p style={{color:'#6b7280',fontSize:13}}>This link has expired. Contact your Nimble representative for an updated status report.</p>
      </div>
    </div>
  );

  if (state === 'error') return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#f0f2f8'}}>
      <div style={{textAlign:'center',background:'#fff',padding:40,borderRadius:10,maxWidth:400}}>
        <div style={{fontSize:40,marginBottom:12}}>🔗</div>
        <h2 style={{fontFamily:'Syne,sans-serif',color:'#000F9F',marginBottom:8}}>Invalid Link</h2>
        <p style={{color:'#6b7280',fontSize:13}}>This link is not valid. Contact your Nimble representative for assistance.</p>
      </div>
    </div>
  );

  const activeLoads = loads.filter(l =>
    ['Open', 'Booked', 'Dispatched','In Transit','Opportunity'].includes(l.status)
  );
  const deliveredLoads = loads.filter(l => l.status === 'Delivered');

  return (
    <div style={{minHeight:'100vh',background:'#f0f2f8',fontFamily:"'DM Sans',Arial,sans-serif"}}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500&family=Syne:wght@700;800&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{background:'#000F9F',padding:'20px 32px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <img src="/nimble-flag-light.png" alt="Nimble" style={{height:48, display:'block'}} />
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{color:'#fff',fontSize:14,fontWeight:600}}>Shipment Status</div>
          <div style={{color:'#6BC4E8',fontSize:11}}>{entity?.name}</div>
        </div>
      </div>

      <div style={{maxWidth:1200,margin:'32px auto',padding:'0 24px'}}>

        {/* Active loads */}
        <div style={{marginBottom:24}}>
          <h2 style={{fontFamily:'Syne,sans-serif',fontSize:16,color:'#000F9F',marginBottom:12}}>
            Active Shipments <span style={{fontFamily:'DM Sans',fontSize:12,color:'#6b7280',fontWeight:400}}>({activeLoads.length})</span>
          </h2>
          {activeLoads.length === 0 ? (
            <div style={{background:'#fff',borderRadius:10,padding:'32px',textAlign:'center',color:'#6b7280',fontSize:13,border:'1px solid #e2e5f0'}}>
              No active shipments at this time
            </div>
          ) : (
            <div style={{background:'#fff',borderRadius:10,overflow:'hidden',border:'1px solid #e2e5f0',boxShadow:'0 1px 3px rgba(0,15,159,0.06)'}}>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead>
                    <tr style={{background:'#000F9F',color:'#fff'}}>
                      {['Pro #','Status','Origin','Destination','Pickup','Delivery','Equipment','Description','Unit #','PO #',''].map(h => (
                        <th key={h} style={{padding:'10px 12px',textAlign:'left',fontSize:11,fontWeight:600,whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeLoads.map((load, i) => (
                      <tr key={load.id} style={{borderBottom:'1px solid #e8ecf5', background: load.priority ? '#fff8e1' : i%2===0 ? '#fff' : '#fafbff'}}>
                        <td style={{padding:'10px 12px',fontFamily:'monospace',fontWeight:600,color:'#000F9F'}}>{load.load_num}</td>
                        <td style={{padding:'10px 12px'}}><StatusPill status={load.status} /></td>
                        <td style={{padding:'10px 12px'}}>{load.origin_city}, {load.origin_state}</td>
                        <td style={{padding:'10px 12px'}}>{load.dest_city}, {load.dest_state}</td>
                        <td style={{padding:'10px 12px',fontSize:12}}>{formatDT(load.pickup_dt)}</td>
                        <td style={{padding:'10px 12px',fontSize:12}}>{formatDT(load.delivery_dt)}</td>
                        <td style={{padding:'10px 12px',fontSize:12}}>{load.equipment}</td>
                        <td style={{padding:'10px 12px',fontSize:12}}>{load.description || '—'}</td>
                        <td style={{padding:'10px 12px',fontSize:12}}>{load.unit_num || '—'}</td>
                        <td style={{padding:'10px 12px', fontSize:12, minWidth:160}}>
                          <POField key={`${load.id}-${sessionKey}`} load={load} onPOAdded={handlePOAdded} />
                        </td>
                        <td style={{padding:'10px 12px',textAlign:'center',color:'#c0392b'}}>{load.priority ? '⚑' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {Object.keys(dirtyLoads).length > 0 && (
          <div style={{display:'flex', justifyContent:'flex-end', marginTop:12}}>
            <button
              onClick={handleSubmitAll}
              disabled={submitting}
              style={{background:'#000F9F', color:'#fff', border:'none',
                      borderRadius:6, padding:'10px 20px', fontSize:13,
                      fontWeight:600, cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: submitting ? 0.6 : 1}}
            >
              {submitting ? 'Submitting...' : `Submit All PO#s (${Object.keys(dirtyLoads).length} load${Object.keys(dirtyLoads).length > 1 ? 's' : ''})`}
            </button>
          </div>
        )}

        {/* Delivered loads */}
        {deliveredLoads.length > 0 && (
          <div style={{marginBottom:24}}>
            <h2 style={{fontFamily:'Syne,sans-serif',fontSize:16,color:'#000F9F',marginBottom:12}}>
              Recent Deliveries <span style={{fontFamily:'DM Sans',fontSize:12,color:'#6b7280',fontWeight:400}}>({deliveredLoads.length} · last 30 days)</span>
            </h2>
            <div style={{background:'#fff',borderRadius:10,overflow:'hidden',border:'1px solid #e2e5f0',boxShadow:'0 1px 3px rgba(0,15,159,0.06)'}}>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead>
                    <tr style={{background:'#000F9F',color:'#fff'}}>
                      {['Pro #','Origin','Destination','Pickup','Delivered','Equipment','Description','Unit #','PO #'].map(h => (
                        <th key={h} style={{padding:'10px 12px',textAlign:'left',fontSize:11,fontWeight:600}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {deliveredLoads.map((load, i) => (
                      <tr key={load.id} style={{borderBottom:'1px solid #e8ecf5', background: i%2===0 ? '#fff' : '#fafbff'}}>
                        <td style={{padding:'10px 12px',fontFamily:'monospace',fontWeight:600,color:'#000F9F'}}>{load.load_num}</td>
                        <td style={{padding:'10px 12px'}}>{load.origin_city}, {load.origin_state}</td>
                        <td style={{padding:'10px 12px'}}>{load.dest_city}, {load.dest_state}</td>
                        <td style={{padding:'10px 12px',fontSize:12}}>{formatDT(load.pickup_dt)}</td>
                        <td style={{padding:'10px 12px',fontSize:12}}>{formatDT(load.delivered_at)}</td>
                        <td style={{padding:'10px 12px',fontSize:12}}>{load.equipment}</td>
                        <td style={{padding:'10px 12px',fontSize:12}}>{load.description || '—'}</td>
                        <td style={{padding:'10px 12px',fontSize:12}}>{load.unit_num || '—'}</td>
                        <td style={{padding:'10px 12px', fontSize:12, minWidth:160}}>
                          <POField key={`${load.id}-${sessionKey}`} load={load} onPOAdded={handlePOAdded} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{textAlign:'center',padding:'24px 0',color:'#9ca3af',fontSize:12}}>
          <p>Questions about your shipments? Contact your Nimble representative.</p>
          <p style={{marginTop:4}}>nimbleinc.net</p>
        </div>
      </div>

      {toast && (
        <div style={{
          position:'fixed', bottom:24, right:24, zIndex:9999,
          background:'#0a6644', color:'#fff', borderRadius:8,
          padding:'12px 20px', fontSize:13, fontWeight:500,
          boxShadow:'0 4px 12px rgba(0,0,0,0.15)',
          animation:'fadein 0.3s ease'
        }}>
          ✓ PO#s submitted to Nimble
          <style>{`@keyframes fadein{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}`}</style>
        </div>
      )}
    </div>
  );
}
